import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { supabase } from '../supabase';
import { emailService } from './emailService';
import { aBlocchi } from './pubblico';
import { leadNotesModel } from '../leadNotes';
import {
  EMAIL_CONSENT_BASIS_LABEL,
  EMAIL_CONTACT_STATUS,
  type EmailContactConListe,
  type EmailContactLink,
  type EmailLista,
} from '../../types/email.types';
import { LEAD_SOURCE_LABELS, LEAD_STAGE_LABELS, type LeadSource, type LeadStage } from '../../types/leads.types';

/**
 * Esporta in Excel i contatti di una lista con tutto quello che il gestionale
 * sa di ciascuno: la riga del Mailing, la scheda cliente collegata e l'ultimo
 * lead collegato, con i campi del modulo che ha compilato.
 *
 * I gettoni di conferma e disiscrizione non escono mai: chi li ha puo'
 * disiscrivere la persona al posto suo.
 */

// Stesso motivo di emailService: le tabelle email_* non sono ancora in database.types.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Righe per richiesta: il server non ne restituisce piu' di mille alla volta. */
const PAGINA = 1000;
/** Identificativi per richiesta: vanno nell'indirizzo, che non puo' essere troppo lungo. */
const BLOCCO_ID = 150;
/** Larghezza massima di una colonna nel foglio, in caratteri. */
const LARGHEZZA_MASSIMA = 50;

const COLONNE_CLIENTE =
  'id, company_name, codice_cliente, status, is_corsista, contact_person, vat_number, tax_code, birth_date, ' +
  'birth_place, address, postal_code, city, province, country, pec_email, sdi_code, partner_consent_at';
const COLONNE_LEAD = 'id, stage, source, form_name, campaign_name, created_at, notes, raw';

export interface ClienteEsportato {
  id: string;
  company_name: string | null;
  codice_cliente: string | null;
  status: string | null;
  is_corsista: boolean | null;
  contact_person: string | null;
  vat_number: string | null;
  tax_code: string | null;
  birth_date: string | null;
  birth_place: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  pec_email: string | null;
  sdi_code: string | null;
  partner_consent_at: string | null;
}

export interface LeadEsportato {
  id: string;
  stage: string;
  source: string;
  form_name: string | null;
  campaign_name: string | null;
  created_at: string;
  notes: string | null;
  raw: Record<string, unknown> | null;
}

export interface DatiEsportazione {
  contatti: EmailContactConListe[];
  collegamenti: Pick<EmailContactLink, 'contact_id' | 'entity_type' | 'entity_id'>[];
  clienti: ClienteEsportato[];
  lead: LeadEsportato[];
  /** id lista -> nome, per scrivere in chiaro le liste di ogni contatto. */
  nomiListe: Record<string, string>;
}

export interface TabellaEsportazione {
  colonne: string[];
  righe: Array<Record<string, string | number>>;
}

type Valore = string | number | null | undefined;
type Coppie = Array<[string, Valore]>;

const STATO_CLIENTE: Record<string, string> = { prospect: 'Prospect', active: 'Attivo', inactive: 'Inattivo' };

function dataOra(iso: string | null | undefined): string | null {
  return iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : null;
}

/** yyyy-mm-dd -> dd/mm/yyyy, senza passare dai fusi orari (una data di nascita non ha ora). */
function soloData(iso: string | null | undefined): string | null {
  const parti = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return parti ? `${parti[3]}/${parti[2]}/${parti[1]}` : iso ?? null;
}

/** Piu' schede cliente con la stessa email: i valori diversi uno accanto all'altro. */
function unisci(valori: Valore[]): string | null {
  const diversi = [...new Set(valori.map((v) => (v === null || v === undefined ? '' : String(v).trim())).filter(Boolean))];
  return diversi.length ? diversi.join(' | ') : null;
}

function campiContatto(c: EmailContactConListe, nomiListe: Record<string, string>): Coppie {
  return [
    ['Email', c.email],
    ['Nome', c.full_name],
    ['Azienda', c.company_name],
    ['Telefono', c.phone],
    ['Città', c.city],
    ['Provincia', c.province],
    ['Stato', EMAIL_CONTACT_STATUS[c.status]?.label ?? c.status],
    ['Base del consenso', c.consent_basis ? EMAIL_CONSENT_BASIS_LABEL[c.consent_basis] ?? c.consent_basis : null],
    ['Consenso dato il', dataOra(c.consent_at)],
    ['Iscrizione confermata il', dataOra(c.confirmed_at)],
    ['Disiscritto il', dataOra(c.unsubscribed_at)],
    ['Motivo disiscrizione', c.unsubscribe_reason],
    ['Liste', c.liste.map((id) => nomiListe[id]).filter(Boolean).join(', ')],
    ['Origine', c.source],
    ['In rubrica dal', dataOra(c.created_at)],
    ['Ultimo invio', dataOra(c.last_sent_at)],
    ['Ultima apertura', dataOra(c.last_opened_at)],
    ['Ultimo clic', dataOra(c.last_clicked_at)],
    ['Ultima risposta', dataOra(c.last_replied_at)],
    ['Rimbalzi', c.bounce_count || null],
    ['Segnalazioni spam', c.complaint_count || null],
  ];
}

function campiExtra(c: EmailContactConListe): Coppie {
  return Object.entries(c.custom_fields ?? {}).map(([chiave, valore]) => [
    chiave,
    typeof valore === 'object' && valore !== null ? JSON.stringify(valore) : (valore as Valore),
  ]);
}

function campiClienti(clienti: ClienteEsportato[]): Coppie {
  const per = (scegli: (c: ClienteEsportato) => Valore) => unisci(clienti.map(scegli));
  return [
    ['Cliente', per((c) => c.company_name)],
    ['Codice cliente', per((c) => c.codice_cliente)],
    ['Stato cliente', per((c) => (c.status ? STATO_CLIENTE[c.status] ?? c.status : null))],
    ['Corsista', clienti.length ? per((c) => (c.is_corsista ? 'Sì' : 'No')) : null],
    ['Referente', per((c) => c.contact_person)],
    ['Partita IVA', per((c) => c.vat_number)],
    ['Codice fiscale', per((c) => c.tax_code)],
    ['Data di nascita', per((c) => soloData(c.birth_date))],
    ['Luogo di nascita', per((c) => c.birth_place)],
    ['Indirizzo', per((c) => c.address)],
    ['CAP', per((c) => c.postal_code)],
    ['Città (cliente)', per((c) => c.city)],
    ['Provincia (cliente)', per((c) => c.province)],
    ['Nazione', per((c) => c.country)],
    ['PEC', per((c) => c.pec_email)],
    ['Codice SDI', per((c) => c.sdi_code)],
    [
      'Consenso aziende partecipanti',
      clienti.length ? per((c) => (c.partner_consent_at ? `Sì (${dataOra(c.partner_consent_at)})` : 'No')) : null,
    ],
  ];
}

function campiLead(lead: LeadEsportato | undefined): { fissi: Coppie; modulo: Coppie } {
  if (!lead) return { fissi: [], modulo: [] };
  const modello = leadNotesModel({ notes: lead.notes, raw: lead.raw });
  return {
    fissi: [
      ['Lead: stato', LEAD_STAGE_LABELS[lead.stage as LeadStage] ?? lead.stage],
      ['Lead: provenienza', LEAD_SOURCE_LABELS[lead.source as LeadSource] ?? lead.source],
      ['Modulo', lead.form_name],
      ['Campagna / corso', lead.campaign_name],
      ['Lead arrivato il', dataOra(lead.created_at)],
      ['Note del lead', modello?.leftover],
    ],
    modulo: modello?.sections.flatMap((s) => s.fields.map((f): [string, Valore] => [f.label, f.value])) ?? [],
  };
}

/** Le colonne fisse, nell'ordine in cui escono: servono anche a capire se un campo del modulo si chiama come una di loro. */
const COLONNE_FISSE = [
  ...campiContatto({ liste: [], status: 'attivo' } as unknown as EmailContactConListe, {}),
  ...campiClienti([]),
  ...campiLead({ stage: '', source: '', created_at: '', notes: null, raw: null } as unknown as LeadEsportato).fissi,
].map(([etichetta]) => etichetta);
const FISSE = new Set(COLONNE_FISSE);

const uguali = (a: Valore, b: Valore) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/**
 * Mette un campo del modulo o un campo extra nella riga. Se si chiama come una
 * colonna fissa (es. "Email" nel modulo del sito) e dice la stessa cosa, si
 * salta; se dice altro prende un prefisso, cosi' non copre il dato della rubrica.
 */
function aggiungiVariabile(riga: Map<string, Valore>, prefisso: string, etichetta: string, valore: Valore): void {
  if (FISSE.has(etichetta) && uguali(riga.get(etichetta), valore)) return;
  const base = FISSE.has(etichetta) ? `${prefisso}: ${etichetta}` : etichetta;
  let nome = base;
  for (let n = 2; riga.has(nome); n++) nome = `${base} (${n})`;
  riga.set(nome, valore);
}

/** Una riga per contatto. Le colonne vuote per tutti spariscono, tranne Email. */
export function tabellaContatti(d: DatiEsportazione): TabellaEsportazione {
  const clientiPerId = new Map(d.clienti.map((c) => [c.id, c]));
  const leadPerId = new Map(d.lead.map((l) => [l.id, l]));

  const mappe = d.contatti.map((c) => {
    const miei = d.collegamenti.filter((l) => l.contact_id === c.id);
    const clienti = miei
      .filter((l) => l.entity_type === 'customer')
      .map((l) => clientiPerId.get(l.entity_id))
      .filter((x): x is ClienteEsportato => !!x);
    const ultimoLead = miei
      .filter((l) => l.entity_type === 'lead')
      .map((l) => leadPerId.get(l.entity_id))
      .filter((x): x is LeadEsportato => !!x)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

    const { fissi, modulo } = campiLead(ultimoLead);
    const riga = new Map<string, Valore>([...campiContatto(c, d.nomiListe), ...campiClienti(clienti), ...fissi]);
    for (const [etichetta, valore] of modulo) aggiungiVariabile(riga, 'Modulo', etichetta, valore);
    for (const [etichetta, valore] of campiExtra(c)) aggiungiVariabile(riga, 'Campo', etichetta, valore);
    return riga;
  });

  const tutte = [...COLONNE_FISSE];
  for (const riga of mappe) for (const nome of riga.keys()) if (!tutte.includes(nome)) tutte.push(nome);
  const piena = (v: Valore) => v !== null && v !== undefined && String(v).trim() !== '';
  const colonne = tutte.filter((nome) => nome === 'Email' || mappe.some((r) => piena(r.get(nome))));

  return {
    colonne,
    righe: mappe.map((r) =>
      Object.fromEntries(colonne.map((nome) => {
        const v = r.get(nome);
        return [nome, piena(v) ? (v as string | number) : ''];
      }))
    ),
  };
}

/** Nome del foglio: Excel accetta al massimo 31 caratteri e nessuno di \ / ? * [ ] : */
export function nomeFoglio(nome: string): string {
  return nome.replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Contatti';
}

function nomeFile(nome: string): string {
  const pulito = nome.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `lista-${pulito || 'contatti'}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
}

function scaricaExcel(tabella: TabellaEsportazione, nomeLista: string): void {
  const foglio = XLSX.utils.json_to_sheet(tabella.righe, { header: tabella.colonne });
  foglio['!cols'] = tabella.colonne.map((nome) => ({
    wch: Math.min(LARGHEZZA_MASSIMA, Math.max(nome.length, ...tabella.righe.map((r) => String(r[nome] ?? '').length)) + 2),
  }));
  // Le frecce dei filtri sull'intestazione: per esempio solo chi ha il consenso per le aziende.
  if (foglio['!ref']) foglio['!autofilter'] = { ref: foglio['!ref'] };

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, foglio, nomeFoglio(nomeLista));
  const contenuto = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([contenuto], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    nomeFile(nomeLista)
  );
}

/** Legge per blocchi di identificativi e mette insieme i risultati. */
async function perBlocchi<T>(
  ids: readonly string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leggi: (blocco: string[]) => PromiseLike<{ data: any[] | null; error: unknown }>
): Promise<T[]> {
  const risultati: T[] = [];
  for (const blocco of aBlocchi([...new Set(ids)], BLOCCO_ID)) {
    const { data, error } = await leggi(blocco);
    if (error) throw error;
    risultati.push(...((data ?? []) as T[]));
  }
  return risultati;
}

async function tuttiIContatti(listaId: string): Promise<EmailContactConListe[]> {
  const contatti: EmailContactConListe[] = [];
  for (let pagina = 0; ; pagina++) {
    const { contatti: pezzo } = await emailService.getContacts({ listaId, page: pagina, pageSize: PAGINA });
    contatti.push(...pezzo);
    if (pezzo.length < PAGINA) return contatti;
  }
}

/** Scarica l'Excel della lista. Ritorna quanti contatti ci sono dentro. */
export async function esportaListaExcel(lista: EmailLista, liste: readonly EmailLista[]): Promise<number> {
  const contatti = await tuttiIContatti(lista.id);
  const collegamenti = await perBlocchi<DatiEsportazione['collegamenti'][number]>(
    contatti.map((c) => c.id),
    (blocco) => db.from('email_contact_links').select('contact_id, entity_type, entity_id').in('contact_id', blocco)
  );
  const idDi = (tipo: string) => collegamenti.filter((l) => l.entity_type === tipo).map((l) => l.entity_id);

  const [clienti, lead] = await Promise.all([
    perBlocchi<ClienteEsportato>(idDi('customer'), (blocco) => db.from('customers').select(COLONNE_CLIENTE).in('id', blocco)),
    perBlocchi<LeadEsportato>(idDi('lead'), (blocco) => db.from('leads').select(COLONNE_LEAD).in('id', blocco)),
  ]);

  const nomiListe = Object.fromEntries(liste.map((l) => [l.id, l.name]));
  scaricaExcel(tabellaContatti({ contatti, collegamenti, clienti, lead, nomiListe }), lista.name);
  return contatti.length;
}
